/**
 * 23. Financial Statements — P&L, Balance Sheet, Cash Flow with period
 * comparison and drill-down to GL.
 *
 * A statement is one set of books, in its legal entity's functional currency.
 * When the workspace keeps books in more than one currency the API answers
 * 422 LEGAL_ENTITY_REQUIRED with the entities, and the page asks which one
 * (docs/design/currency.md R6, CUR-2).
 *
 * Arabic and English (C7): the tabs are chosen by the statement's code,
 * never by their words; the statement's title, its "To date" period and its
 * section lines (one per account type) are the catalog's words, keyed by the
 * codes the API sends; account names are the workspace's data. Indentation
 * and the amount column are logical (inline-start, end), so the statement
 * reads from the right in Arabic.
 *
 * The Cash Flow tab (REP-CASH, D461): the API refuses it with 422
 * CAPABILITY_NOT_BUILT naming the slice that builds it (AE-ST2), and the tab
 * says "not available yet" in place of the statement (./not-available.tsx).
 * It used to show every asset account's balance under the title Cash Flow.
 *
 * The Profit & Loss period (REP-PL): the API reads this statement with no
 * date predicate, so it covers every posted entry, whatever its date,
 * including any dated after today. Its badge used to say "To date", which
 * that is not. It now says "All dates", with a persistent line saying what
 * that means, until the statement engine lets a period be chosen. The badge
 * is chosen by the statement's code; `period` "To date" is the API's marker
 * for "no period filter".
 */
import { useState } from "react";
import { Card, PageHeader, Skeleton, Tabs, Badge } from "@/components/ui";
import { PendingButton } from "@/components/PendingAction";
import {
  LegalEntityPicker,
  legalEntitiesRequired,
  retryUnlessLegalEntityRequired,
  useOfferedLegalEntities,
  withLegalEntity,
} from "@/components/LegalEntityPicker";
import { QueryError } from "@/components/QueryError";
import { useApiQuery } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { formatMoney } from "@/lib/money";
import { NotAvailableReport, notAvailableOf } from "./not-available";

type StatementKey = "ProfitAndLoss" | "BalanceSheet" | "CashFlow";

interface StatementLine {
  code: string;
  label: string;
  amount: string;
  level: number;
}

interface StatementData {
  statement: StatementKey;
  title: string;
  period: string;
  currencyCode: string;
  lines: StatementLine[];
  total: string;
}

const STATEMENTS: StatementKey[] = [
  "ProfitAndLoss",
  "BalanceSheet",
  "CashFlow",
];

/** Level-0 lines are headed by an account type (composed-views.controller.ts). */
const SECTIONS = new Set([
  "Asset",
  "Liability",
  "Equity",
  "Revenue",
  "Expense",
]);

export function FinancialStatementsPage() {
  const { t } = useTranslation();
  const [active, setActive] = useState<StatementKey>("ProfitAndLoss");
  const [chosenEntity, setChosenEntity] = useState("");
  const { data, isPending, error, refetch, isFetching } =
    useApiQuery<StatementData>(
      ["finance", "financial-statements", active, chosenEntity],
      withLegalEntity(
        `/finance/financial-statements/${active.toLowerCase()}`,
        chosenEntity,
      ),
      {
        // A refusal that says "not available yet" is an answer: asking
        // again cannot change it, so it is shown at once.
        retry: (failureCount, err) =>
          notAvailableOf(err) === null &&
          retryUnlessLegalEntityRequired(failureCount, err),
      },
    );
  const notAvailable = notAvailableOf(error);
  const entities = useOfferedLegalEntities(error);
  // Refused for want of a legal entity: the picker above says so and asks.
  const askingForEntity =
    (legalEntitiesRequired(error)?.length ?? 0) > 0 && !chosenEntity;

  const statementTitle = (d: StatementData) =>
    STATEMENTS.includes(d.statement)
      ? t(`finance.statements.tabs.${d.statement}`)
      : d.title;
  /** The period it covers, said plainly (REP-PL). */
  const undated = (d: StatementData) => d.period === "To date";
  const periodLabel = (d: StatementData) =>
    !undated(d)
      ? d.period
      : d.statement === "ProfitAndLoss"
        ? t("finance.statements.allDates")
        : t("finance.statements.toDate");
  const lineLabel = (line: StatementLine) =>
    line.level === 0 && SECTIONS.has(line.code)
      ? t(`finance.statements.sections.${line.code}`, {
          defaultValue: line.label,
        })
      : line.label;

  return (
    <div>
      <PageHeader
        title={t("finance.statements.title")}
        subtitle={t("finance.statements.subtitle")}
        actions={
          <PendingButton reason="statements" slice="AE-W3">
            {t("finance.statements.compare")}
          </PendingButton>
        }
      />
      <Tabs
        tabs={STATEMENTS.map((key) => ({
          id: key,
          label: t(`finance.statements.tabs.${key}`),
        }))}
        active={active}
        onChange={(tab) => {
          if (STATEMENTS.includes(tab as StatementKey))
            setActive(tab as StatementKey);
        }}
      />
      <LegalEntityPicker
        entities={entities}
        value={chosenEntity}
        onChange={setChosenEntity}
      />
      {isPending ? (
        <Skeleton rows={14} />
      ) : notAvailable ? (
        <NotAvailableReport
          what={notAvailable}
          title={t(`finance.statements.tabs.${active}`)}
        />
      ) : askingForEntity ? null : error || !data ? (
        <QueryError
          error={error}
          retrying={isFetching}
          onRetry={() => void refetch()}
        />
      ) : (
        <Card
          title={statementTitle(data)}
          actions={<Badge tone="primary">{periodLabel(data)}</Badge>}
        >
          {undated(data) && data.statement === "ProfitAndLoss" ? (
            <p className="atlas-muted" role="note">
              {t("finance.statements.allDatesNote")}
            </p>
          ) : null}
          <table className="atlas-table">
            <tbody>
              {data.lines.map((line, i) => (
                <tr key={`${line.code}-${i}`}>
                  <td style={{ paddingInlineStart: 16 + line.level * 20 }}>
                    {lineLabel(line)}
                  </td>
                  <td
                    className="is-numeric"
                    style={{
                      textAlign: "end",
                      fontFamily: "var(--atlas-mono)",
                    }}
                  >
                    {formatMoney(line.amount, data.currencyCode)}
                  </td>
                </tr>
              ))}
              <tr>
                <td>
                  <strong>{statementTitle(data)}</strong>
                </td>
                <td className="is-numeric" style={{ textAlign: "end" }}>
                  <strong>{formatMoney(data.total, data.currencyCode)}</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
